// Retry policy (contract 03 §5) — the last open acceptance bullet of the createHttpClient
// port. v3 configured none at all, so a single flaky hop surfaced as a toast whose only
// remedy was a reload.
//
// Deliberately narrow. Only GET replays: it is the only method where replaying cannot ask
// whether the server already applied a write. The trigger set is the ticket's — transport
// failures (`network`/`timeout`) and 503 — plus 429, which is here because the acceptance
// asks for `Retry-After` to be honoured, and a status that is never retried has no delay
// to honour. `canceled` never retries; an aborted request is a decision, not a failure.
//
// The named delay is read once, in `ApiError.fromAxios` (`retryAfterMs`), because the header
// outlives this decision: feature-chat's 429 (ORP-20465) rides a POST that is never replayed and
// still has to tell the user how long to wait. Absent, empty or unparseable stays null and falls
// back to the fixed backoff below.
import type { InternalAxiosRequestConfig } from 'axios'

import type { ApiError } from './ApiError'

/** Extra attempts after the first. */
export const MAX_RETRIES = 2

/** Backoff when the server named no delay: 300ms before the first retry, 600ms before the second. */
const BASE_DELAY_MS = 300

/**
 * A `Retry-After` longer than this is honoured by NOT retrying. Replaying a GET seconds
 * after the user's screen has moved on is worse than surfacing the error while it is still
 * about the thing they are looking at.
 */
const MAX_HONOURED_DELAY_MS = 5_000

const RETRYABLE_STATUSES = new Set([429, 503])

/**
 * Decide whether to replay this request and how long to wait first; null means fail now.
 *
 * Recording the attempt is part of the decision on purpose: there is exactly one caller, and
 * a caller that asked without retrying would burn a slot for nothing.
 *
 * The count rides on the config rather than a WeakMap keyed by it, because the replay goes
 * back through `instance.request(config)` and axios merges that into a *new* config object —
 * a map keyed by identity resets on every attempt and the budget never runs out.
 */
export function planRetry(
  apiError: ApiError,
  config: InternalAxiosRequestConfig | undefined,
): number | null {
  if (!config || !isRetryable(apiError, config)) return null

  const used = config.retryCount ?? 0
  if (used >= MAX_RETRIES) return null

  const named = apiError.retryAfterMs
  // The server named a wait we are not willing to hold the request for.
  if (named !== null && named > MAX_HONOURED_DELAY_MS) return null

  config.retryCount = used + 1
  return named ?? BASE_DELAY_MS * 2 ** used
}

function isRetryable(apiError: ApiError, config: InternalAxiosRequestConfig): boolean {
  // Opt-out mirrors `suppressGlobalErrorHandler`: the bare flag or the typed meta field.
  if (config.retry === false || config.meta?.retry === false) return false
  if ((config.method ?? 'get').toLowerCase() !== 'get') return false
  if (apiError.kind === 'canceled') return false
  if (apiError.kind === 'network' || apiError.kind === 'timeout') return true
  return apiError.status !== null && RETRYABLE_STATUSES.has(apiError.status)
}
